import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateDielec } from "@/api/hooks/useDielce";
import { useCennikMaterialy } from "@/api/hooks/useCennik";
import {
  hrubkaMaterialu,
  nesuladHrubkySlotu,
  predvolenaHrubkaDosky,
  useDostupneHrubky,
} from "@/api/hooks/useDostupneHrubky";
import { HrubkaDoskySelect } from "@/components/komponent/HrubkaDoskySelect";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { MEDZERA_CIEL } from "@/lib/korpus/types";
import {
  BOX_CHRBAT_NIZSI,
  DNO_NL_ODPOCET,
  boxSirkaOdpocet,
  dnoSirkaOdpocet,
  vyberNlVysuvu,
  vyskaBokuZasuvky,
} from "@/lib/korpus/generator";
import type { TypCela } from "@/lib/korpus/types";
import type { MaterialSlot, Zostava } from "@/api/schemas";

export interface ZasuvkaDialogProps {
  zakazkaId: string;
  /** Zostavy varianty — user si vyberie, do ktorej zásuvku vloží. */
  zostavy: Zostava[];
  /** Predvolená zostava (výber v editore). */
  selectedZostavaId?: string | null;
  sloty: MaterialSlot[];
  open: boolean;
  onOpenChange: (o: boolean) => void;
}

/**
 * Samostatná zásuvka do existujúcej zostavy — zadáva sa ROZMER OTVORU
 * korpusu. Rozmery boxu podľa NAUČENÝCH TopSolid vzorcov (07/2026):
 * výška boku auto (naložené (V−240)/N, vložené V/N−40), čelo/chrbát
 * boxu = otvor − (2×13 + 2t + 1) (chrbát −14 nižší), dno sololit 4 mm
 * (NL−10) × (otvor − (2×13 + 2t − 15)) — pri 18 mm −63 a −47; čelá
 * podľa §14, výsuv NL auto podľa hĺbky.
 */
export function ZasuvkaDialog({
  zakazkaId,
  zostavy,
  selectedZostavaId,
  sloty,
  open,
  onOpenChange,
}: ZasuvkaDialogProps): JSX.Element {
  const [zostavaId, setZostavaId] = useState(
    selectedZostavaId ?? zostavy[0]?.id ?? "",
  );
  const [sirka, setSirka] = useState("564"); // šírka otvoru
  const [vyska, setVyska] = useState("200"); // výška otvoru (čelo)
  const [hlbka, setHlbka] = useState("560"); // hĺbka korpusu
  const [vyskaBoku, setVyskaBoku] = useState(""); // "" = auto (naučený vzorec)
  const [pocet, setPocet] = useState("1");
  const [typ, setTyp] = useState<TypCela>("nalozene");
  const hrubky = useDostupneHrubky();
  const hrubkaSlotu = (id: string): number | null =>
    hrubkaMaterialu(sloty.find((sl) => sl.id === id)?.material ?? null);
  const [slotId, setSlotId] = useState(sloty[0]?.id ?? "");
  // Box aj čelá sú z dosky slotu (KOLO 168) — natvrdo 18 mm zásuvku zo
  // 16 mm slotu server odmietol (422)
  const [hrubka, setHrubka] = useState(() =>
    String(predvolenaHrubkaDosky(hrubkaSlotu(sloty[0]?.id ?? ""), hrubky)),
  );
  const [vysuvMat, setVysuvMat] = useState<{ id: string; nazov: string } | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const createDielec = useCreateDielec(zakazkaId, zostavaId);

  /** Zmena slotu nastaví hrúbku jeho materiálu — ako dielec (KOLO 159). */
  function zmenSlot(id: string): void {
    setSlotId(id);
    const z = hrubkaSlotu(id);
    if (z != null) setHrubka(String(z));
  }

  // default výsuv: prvý obľúbený z kategórie Výsuvy
  const vysuvDefaultQuery = useCennikMaterialy(
    {
      sekcia: "kovanie",
      vyber: true,
      je_oblubene: true,
      // rodič skupiny vráti aj podskupiny (guličkové, box…) — server
      kategoria: "KOVANIE_VYSUV",
      per_page: 1,
    },
    { enabled: open },
  );
  useEffect(() => {
    const m = vysuvDefaultQuery.data?.data?.[0];
    if (m && !vysuvMat) setVysuvMat({ id: m.id, nazov: m.nazov });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vysuvDefaultQuery.data]);

  const s = parseFloat(sirka) || 564;
  const v = parseFloat(vyska) || 200;
  const h = parseFloat(hlbka) || 560;
  const n = Math.max(1, parseInt(pocet, 10) || 1);
  const t = parseFloat(hrubka) || 18;
  const hrubkaNesedi = nesuladHrubkySlotu(t, hrubkaSlotu(slotId));
  // výška boku boxu: auto podľa naučeného vzorca, ručný override možný
  const vbAuto = vyskaBokuZasuvky(v, n, typ);
  const vb =
    vyskaBoku.trim() !== ""
      ? Math.max(40, parseFloat(vyskaBoku) || vbAuto)
      : vbAuto;
  // čelo podľa typu (§14): naložené/vložené vstup −2/strana,
  // polonaložené +2×(t/2−1); n čiel delí výšku s 2 mm medzerou
  const pridaj = typ === "polonalozene" ? 2 * (t / 2 - 1) : -4;
  const sirkaCela = Math.round(s + pridaj);
  const vyskaCela = Math.round((v + pridaj - (n - 1) * MEDZERA_CIEL) / n);
  const nl = vyberNlVysuvu(h);
  const boxSirka = Math.round(s - boxSirkaOdpocet(t));
  const dnoSirka = Math.round(s - dnoSirkaOdpocet(t));

  async function handleCreate(): Promise<void> {
    if (!zostavaId || hrubkaNesedi) return;
    if (nl === null) {
      toast({
        variant: "destructive",
        title: "Hĺbka nestačí",
        description: `Hĺbka ${h} mm nestačí ani na najkratší výsuv (250 + 4 mm).`,
      });
      return;
    }
    setSubmitting(true);
    try {
      const suffix = Date.now().toString(36).slice(-4);
      const dielce = [
        // čelá aj sololit dno bez CNC nadmiery (len porez), box s nadmierou
        { kod: `zas_${suffix}_celo`, x: sirkaCela, y: vyskaCela, z: t, ks: n, smer: "Y" as const, cnc: false },
        { kod: `zas_${suffix}_bok`, x: nl, y: vb, z: t, ks: 2 * n, smer: "X" as const, cnc: true },
        { kod: `zas_${suffix}_box_celo`, x: boxSirka, y: vb, z: t, ks: n, smer: "X" as const, cnc: true },
        { kod: `zas_${suffix}_box_chrbat`, x: boxSirka, y: vb - BOX_CHRBAT_NIZSI, z: t, ks: n, smer: "X" as const, cnc: true },
        { kod: `zas_${suffix}_dno`, x: nl - DNO_NL_ODPOCET, y: dnoSirka, z: 4, ks: n, smer: "X" as const, cnc: false },
      ];
      for (const d of dielce) {
        await createDielec.mutateAsync({
          kod: d.kod,
          nazov_strojovy: d.kod.replace(`_${suffix}`, ""),
          pocet_ks: d.ks,
          x_mm: String(d.x),
          y_mm: String(d.y),
          z_mm: String(d.z),
          material_slot_id: d.z === 4 ? null : slotId || null,
          material_id_direct: null,
          nadmiera_x_mm: "5.00",
          nadmiera_y_mm: "5.00",
          nadmiera_aktivna: d.cnc,
          // Bez postupu rozhoduje override (KOLO 108) — pozri KorpusBuilderV2.
          ide_na_cnc: d.cnc,
          smer_vlakien: d.smer,
          hrana_x1_auto: true,
          hrana_x2_auto: true,
          hrana_y1_auto: true,
          hrana_y2_auto: true,
          poradie: 0,
        });
      }
      // výsuvy: 1 pár na zásuvku
      await createDielec.mutateAsync({
        kod: `zas_${suffix}_vysuv`,
        nazov_strojovy: vysuvMat?.nazov ?? `Výsuv NL ${nl} (vyber z cenníka)`,
        pocet_ks: n,
        x_mm: null,
        y_mm: null,
        z_mm: null,
        material_slot_id: null,
        material_id_direct: vysuvMat?.id ?? null,
        nadmiera_x_mm: "0",
        nadmiera_y_mm: "0",
        nadmiera_aktivna: false,
        smer_vlakien: "0",
        hrana_x1_auto: false,
        hrana_x2_auto: false,
        hrana_y1_auto: false,
        hrana_y2_auto: false,
        poradie: 0,
      });
      toast({
        title: `Zásuvka vytvorená (${n} ks + ${n}× pár výsuvov NL ${nl})`,
        ...(vysuvMat
          ? {}
          : {
              variant: "destructive" as const,
              description: "Výsuv sa nenašiel v cenníku — dielec je bez materiálu.",
            }),
      });
      onOpenChange(false);
    } catch (err) {
      toast({ variant: "destructive", title: "Chyba", description: extractErrorMessage(err) });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Vytvoriť zásuvku</DialogTitle>
          <DialogDescription className="sr-only">
            Zásuvky do vybraného otvoru vrátane výsuvov a čielok.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Do zostavy</Label>
            <Select value={zostavaId} onValueChange={setZostavaId}>
              <SelectTrigger aria-label="Cieľová zostava"><SelectValue placeholder="Vyber zostavu…" /></SelectTrigger>
              <SelectContent>
                {zostavy.map((z) => (
                  <SelectItem key={z.id} value={z.id}>{z.kod} {z.nazov}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Šírka otvoru</Label><Input type="number" value={sirka} onChange={(e) => setSirka(e.target.value)} aria-label="Šírka otvoru" /></div>
            <div><Label>Výška otvoru</Label><Input type="number" value={vyska} onChange={(e) => setVyska(e.target.value)} aria-label="Výška otvoru" /></div>
            <div><Label>Hĺbka korpusu</Label><Input type="number" value={hlbka} onChange={(e) => setHlbka(e.target.value)} aria-label="Hĺbka korpusu" /></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Počet zásuviek</Label><Input type="number" min={1} value={pocet} onChange={(e) => setPocet(e.target.value)} aria-label="Počet zásuviek" /></div>
            <div>
              <Label>
                Výška boku <span className="text-muted-foreground">(auto {vbAuto})</span>
              </Label>
              <Input
                value={vyskaBoku}
                placeholder="auto"
                onChange={(e) => setVyskaBoku(e.target.value)}
                inputMode="numeric"
                aria-label="Výška boku zásuvky"
              />
            </div>
            <div>
              <Label>Čelá</Label>
              <Select value={typ} onValueChange={(v2) => setTyp(v2 as TypCela)}>
                <SelectTrigger aria-label="Typ čiel"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="nalozene">naložené</SelectItem>
                  <SelectItem value="vlozene">vložené</SelectItem>
                  <SelectItem value="polonalozene">polonaložené</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <Label>Materiál (box + čelá)</Label>
              <Select value={slotId} onValueChange={zmenSlot}>
                <SelectTrigger aria-label="Materiál"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {sloty.map((sl) => (
                    <SelectItem key={sl.id} value={sl.id}>{sl.slot_kod} {sl.label ?? ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <HrubkaDoskySelect
              label="Hrúbka"
              ariaLabel="Hrúbka boxu a čiel"
              value={hrubka}
              onChange={setHrubka}
              matZ={hrubkaSlotu(slotId)}
              hrubky={hrubky}
            />
          </div>
          {hrubkaNesedi ? (
            <p className="rounded-md border border-destructive bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {hrubkaNesedi}
            </p>
          ) : null}
          <p className="rounded-md border bg-background-alt/40 px-3 py-2 text-xs text-muted-foreground">
            Čelo {sirkaCela} × {vyskaCela} ×{n}, box {boxSirka} × {vb}
            (chrbát −{BOX_CHRBAT_NIZSI}),{" "}
            {nl !== null ? (
              <>
                výsuv NL <b>{nl}</b> ({vysuvMat ? vysuvMat.nazov : <span className="text-destructive">nenájdený v cenníku</span>}),
                dno sololit 4 mm {nl - DNO_NL_ODPOCET} × {dnoSirka}
              </>
            ) : (
              <span className="text-destructive">hĺbka nestačí na výsuv!</span>
            )}
            {" "}— vzorce naučené z reálnych TopSolid exportov
          </p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Zrušiť</Button>
          <Button
            onClick={handleCreate}
            disabled={submitting || !zostavaId || nl === null || hrubkaNesedi !== null}
            title={hrubkaNesedi ?? undefined}
          >
            {submitting ? "Vytvára sa…" : "Vytvoriť"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
